// Stale-chunk detection and the reload guard, shared by router.onError
// (route chunks) and the vite:preloadError boot handler (every other lazy
// import: flowsheet dialogs, Summary, TipTap, cabinet, ...), so there is one
// implementation of "reload once, never loop".

// A chunk 404 after a deploy is rewritten to a 200 text/html response by
// CloudFront's SPA fallback (infra/site.js) rather than a real 404 — an
// accepted tradeoff there so transient asset-upload races don't turn into a
// sticky white screen. A tab left open across a full deploy hits the same
// fallback for a chunk that's gone for good, and each browser engine phrases
// the resulting module-load failure differently.
export function isStaleChunkError(error) {
  const message = error?.message || ''
  return (
    message.includes('is not a valid JavaScript MIME type') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Importing a module script failed')
  )
}

const CHUNK_RELOAD_KEY_PREFIX = 'chartflow-chunk-reload-'

// How long one recorded attempt blocks another reload of the same path for the
// same build. Long enough that an unkillable failure (a cached index.html that
// keeps pointing at a dead chunk) cannot loop faster than once per window;
// short enough that a tab which exhausted its attempt does not stay broken for
// the rest of the session. (Issue 1202: the old guard was write-once per path
// and only cleared by a completed navigation to that exact path, so a failed
// recovery blocked every later attempt for the life of the tab.)
export const CHUNK_RELOAD_WINDOW_MS = 5 * 60 * 1000

// Identifies the build the running bundle came from (CI short SHA). A reload
// that lands on a NEW build gets a fresh allowance; a reload that lands on the
// same build (stale cached index.html) does not.
const currentBuild = () => import.meta.env.VITE_VERSION || 'unknown'

const chunkReloadKey = (path) => `${CHUNK_RELOAD_KEY_PREFIX}${path}`

// Record that we have tried to recover this path and report whether this
// attempt may proceed. Without a bound, recovery would be unbounded: a full
// document load re-fetches index.html, and if the browser or an edge hands back
// the cached copy it still points at the dead chunk hash, throws the same
// error, and reloads again. sessionStorage survives the document load, which is
// the point: the guard has to outlive the very reload it is bounding.
//
// An attempt is blocked only when a record exists for this path from THIS build
// and is younger than CHUNK_RELOAD_WINDOW_MS. Records from another build, older
// records, and legacy '1' records are replaced.
//
// sessionStorage is not always reachable (sandboxed LMS iframe throws
// SecurityError on access; old Safari private mode threw on setItem). Then we
// decline to reload: an unguarded reload is an unkillable loop. The caller
// rethrows / lets the ORIGINAL error surface, so Datadog still sees the cause.
export function shouldAttemptStaleChunkReload(path, now = Date.now()) {
  const key = chunkReloadKey(path)
  const build = currentBuild()
  try {
    const raw = sessionStorage.getItem(key)
    if (raw) {
      let record = null
      try {
        record = JSON.parse(raw)
      } catch {
        // Legacy '1' or garbage: treated as expired below.
      }
      if (record && record.build === build && now - record.at < CHUNK_RELOAD_WINDOW_MS) {
        console.warn('Stale chunk reload already attempted for', path, '- not retrying')
        return false
      }
    }
    sessionStorage.setItem(key, JSON.stringify({ build, at: now }))
    return true
  } catch (storageError) {
    console.warn(
      'sessionStorage unavailable, cannot bound a stale-chunk reload for',
      path,
      storageError
    )
    return false
  }
}

// Disarm the guard for a path we have now loaded successfully (runs after every
// completed navigation). A storage failure is silent on purpose: if storage is
// unreachable no key was ever written, and warning would spam once per
// navigation; the sandboxed case is reported once, where it changes behaviour.
export function clearStaleChunkReloadGuard(path) {
  try {
    sessionStorage.removeItem(chunkReloadKey(path))
  } catch {
    // Intentionally ignored — see above.
  }
}
